// Mirrors server/utils/colorBuckets.ts (names must match what's stored in
// the `colors` column) — kept as a small separate copy since Nuxt doesn't
// share code between app/ and server/ without a shared/ dir.
export interface ColorBucket {
  name: string;
  hex: string;
}

export const COLOR_BUCKETS: ColorBucket[] = [
  { name: "Red", hex: "#c81e1e" },
  { name: "Orange", hex: "#e67814" },
  { name: "Yellow", hex: "#e6c828" },
  { name: "Green", hex: "#3c8c3c" },
  { name: "Blue", hex: "#285ac8" },
  { name: "Purple", hex: "#7830b4" },
  { name: "Pink", hex: "#eb91b4" },
  { name: "Gold", hex: "#bea03c" },
  { name: "Black", hex: "#141414" },
  { name: "White", hex: "#f5f5f5" },
  { name: "Grey", hex: "#808080" },
  { name: "Beige", hex: "#d2b491" },
  { name: "Brown", hex: "#6e4226" },
  { name: "Multicolor", hex: "#808080" },
];

// Reads the `colors` URL param (e.g. "red,Blue,Foo") into valid color names:
// matching ignores case ("red" -> "Red"), unknown names are dropped and
// duplicates removed. Returns undefined when nothing valid is left, so a bad
// link falls back to the unfiltered page instead of "No artworks found".
export function parseColorsParam(raw: unknown): string[] | undefined {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== "string") return undefined;
  const result: string[] = [];
  for (const part of value.split(",")) {
    const name = part.trim().toLowerCase();
    if (!name) continue;
    const bucket = COLOR_BUCKETS.find((b) => b.name.toLowerCase() === name);
    if (bucket && !result.includes(bucket.name)) result.push(bucket.name);
  }
  return result.length > 0 ? result : undefined;
}

// Inline style for a color's swatch dot (Multicolor gets a rainbow gradient).
export function colorSwatchStyle(name: string): Record<string, string> {
  if (name === "Multicolor") {
    return { background: "conic-gradient(from 0deg, #e02424, #e6c828, #3c8c3c, #285ac8, #7830b4, #e02424)" };
  }
  const bucket = COLOR_BUCKETS.find((b) => b.name === name);
  return { backgroundColor: bucket?.hex ?? "#808080" };
}
